<template>
  <v-sheet height="100%" color="bg1" width="100%">
    <v-sheet height="100%" class="rounded-tl-lg overflow-hidden pa-4 d-flex flex-column">
      <div
        class="pa-2 d-flex align-center justify-space-between"
        style="gap: 8px"
        :class="{
          'flex-wrap': $vuetify.breakpoint.xs,
        }">
        <div class="d-flex align-center">
          <h3 class="me-3">Levels &amp; Areas</h3>
          <v-sheet
            :class="{ 'order-last': $vuetify.breakpoint.xs }"
            :width="$vuetify.breakpoint.xs ? '100%' : 250">
            <v-text-field
              hide-details
              outlined
              v-model="searchtext"
              dense
              class="search-bar"
              placeholder="Search..."
              clearable
              prepend-inner-icon="mdi-magnify"></v-text-field>
          </v-sheet>
        </div>

        <v-btn @click="editLevel()" v-has="permissions.AdminFacilityLevels.Create" color="primary"
          ><v-icon left>mdi-plus</v-icon> Add Level
        </v-btn>
      </div>
      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table flex-grow-1"
        :headers="LevelHeaders"
        :items="shownLevels"
        hide-default-footer
        disable-pagination
        @click:row="showPlanView"
        show-select
        :single-select="true"
        :sort-by="['bottomElevation']"
        :sort-desc="[false]">
        <template slot="item.data-table-select" slot-scope="props">
          <tr @click.stop="updateSelectedLevel(props.item)">
            <td>
              <v-radio-group dense v-model="selectedLevel" name="rowSelector">
                <v-radio dense :value="props.item.id" />
              </v-radio-group>
            </td>
          </tr>
        </template>
        <template v-slot:[`item.name`]="{ item }">
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 d-flex align-items-center">
              <b class="align-self-center pl-2 flex-grow-1">{{ item.name }}</b>
            </div>
            <div class="align-self-center mx-3">
              <v-progress-circular
                color="primary"
                v-if="item.isUploading"
                indeterminate
                :width="5"></v-progress-circular>
            </div>
            <div
              v-has="permissions.AdminFacilityLevels.Update"
              style="display: flex"
              @click.stop=""
              title="Open in 3D">
              <v-checkbox
                class="m-0"
                label="Open in 3D"
                v-model="item.openIn3D"
                @check_changed="toggleOpenIn3D(item, $event)">
              </v-checkbox>
            </div>
            <div
              class="level-dbids align-self-center mx-3"
              :style="{ 'background-color': item.elementsCount > 0 ? 'green' : 'lightgray' }">
              {{ item.elementsCount }} Objects
            </div>
          </div>
        </template>

        <template v-slot:[`item.bottomElevation`]="{ item }">
          {{ item.bottomElevation != null ? `${Number(item.bottomElevation).toFixed(2)}m` : '—' }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon elevation="0" v-bind="attrs" v-on="on">
                <v-icon size="16px">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list class="more-options-menu" dense>
              <v-list-item @click.stop="ShowIn3D(item)" title="Show in 3D" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="warning">mdi-cube</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Show in 3D</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click.stop="resetMapping(item)" title="Reset Mapping" v-if="item.hasAllMappingInfo()" dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="primary">mdi-refresh</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Reset Mapping</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                @click.stop="uploadLevelPlan(item)"
                v-has="permissions.AdminFacilityLevels.Update"
                dense>
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-upload</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Upload Level Plan
                    <input
                      type="file"
                      :id="'uploadLevelPlanInput' + item.id"
                      style="display: none"
                      accept="image/png, image/jpeg, image/gif, application/pdf"
                      @click.stop=""
                      @change="onSelectImage($event, item)" />
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                @click.stop="editLevel(item)"
                dense
                title="Edit Level"
                v-has="permissions.AdminFacilityLevels.Update">
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="primary">mdi-pencil-box-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title> Edit Level </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                @click.stop="deleteLevel(item)"
                dense
                title="Delete Level"
                v-has="permissions.AdminFacilityLevels.Delete">
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title> Delete Level </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No Data Available!</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading Levels...</p>
          </div>
        </template>
      </v-data-table>
      <EditLevel ref="EditLevel"></EditLevel>
    </v-sheet>
  </v-sheet>
</template>

<script>
  import { toggleExpandComponent } from '../../../scripts/js/forgeviewer';
  import { checkIsModelTrasnlated } from '@Utils/forgeUtils';
  import levelsService from '@/features/Admin/services/levels.service';
  import $ from 'jquery';
  import permissions from '@Share/config/permissions';
  import EditLevel from '@/components/admin/popups/editLevel.vue';
  import LevelHeaders from '../config/models.headers';
import { MappingViewersInfo } from '@/scripts/js/forgeModels';

  export default {
    name: 'AdminLevels',

    emits: ['toggle-level-in-3d', 'launch-viewer', 'update_levels'],
    data() {
      return {
        permissions,
        isExpanded: false,
        searchtext: '',
        LevelHeaders,
      };
    },
    methods: {
      toggleOpenIn3D(level, $event) {
        level.openIn3D = $event.value;
        levelsService.editLevel(this.$route.params.id, level);
      },
      updateSelectedLevel(item) {
        this.selectedLevel = item?.id;
        this.$store.state.forgeService.saveSelectedLevel(this.$route.params.id, item.name);
      },
      toggleExpanded() {
        toggleExpandComponent(this);
      },
      ShowIn3D(level) {
        if (!level.planImageUrl) {
          this.$dialog.notify.warning('Old data found, please upload the plan again.', {
            position: 'top-right',
            timeout: 3000,
          });

          return;
        }
        this.$emit('toggle-level-in-3d', level);
      },
      showPlanView(level) {
        if (!level.planUrn) {
          this.$dialog.notify.warning('Please upload a plot plan first.', {
            position: 'top-right',
            timeout: 3000,
          });

          return;
        }
        if (!level.planImageUrl) {
          this.$dialog.notify.warning('Old data found, please upload the plan again.', {
            position: 'top-right',
            timeout: 3000,
          });

          return;
        }
        var _this = this;
        checkIsModelTrasnlated(level.planUrn, level.apsRegion, (isTranslated) => {
          if (!isTranslated) {
            this.$dialog.notify.warning('Plot plan not yet translated, please wait.', {
              position: 'top-right',
              timeout: 3000,
            });

            return;
          }
          if (isTranslated) {
            _this.$emit('launch-viewer-2d', level);
          }
        });
      },
      uploadLevelPlan(level) {
        if (level.isUploading) {
          this.$dialog.notify.warning('Please wait until uploading is done.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        if (!level) {
          this.$dialog.notify.warning('Please select a level first.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        $('#uploadLevelPlanInput' + level.id).click();
      },
      resetMapping(level) {
        this.$dialog.error({
          text: `Are you sure you want to reset mapping for this level?`,
          title: `Reset Mapping`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                level.mappingViewersPointsInfo = new MappingViewersInfo();
                levelsService.editLevel(this.$route.params.id, level).then(() => {
                  this.$dialog.notify.success('Mapping reset successfully.', {
                    position: 'top-right',
                    timeout: 3000,
                  });

                  if (this.$store.state.selectedLevel?.id == level.id) {
                    this.$store.state.selectedLevel.mappingViewersPointsInfo = new MappingViewersInfo();
                  }
                });
              },
            },
          },
        });
      },
      onSelectImage(event, level) {
        var file = event.target.files[0];
        let formData = new FormData();
        formData.append('fileToUpload', file);
        formData.append('assetId', this.$route.params.id);
        formData.append('levelId', level.id);
        formData.append('oldUrn', level.planUrn);
        formData.append('oldImage', level.planImageUrl);
        $('#uploadLevelPlanInput' + level.id).val('');

        level.isUploading = true;
        levelsService.uploadLevelPlan(formData)
          .then((response) => {
            let { data } = response;
            level.isUploading = false;
            level.planUrn = data.planUrn;
            level.planImageUrl = data.planImageUrl;
          })
          .catch((err) => {
            level.isUploading = false;
            this.$dialog.notify.warning(err, {
              position: 'top-right',
              timeout: 3000,
            });
          });
      },
      deleteLevel(level) {
        this.$dialog.error({ 
          text: `Are you sure you want to delete level?`,
          title: `Delete Level`,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                levelsService.deleteLevel(level.id, this.$route.params.id);
                var index = this.levels.findIndex((o) => o == level);
                this.$emit(
                  'update_levels',
                  this.levels.slice(0, index).concat(this.levels.slice(index + 1)),
                );
              },
            },
          },
        });
      },

      editLevel(level) {
        this.$refs.EditLevel.open(level);
      },
    },
    computed: {
      levels() {
        return this.$store.state.levels;
      },
      shownLevels: function () {
        let lower = this.searchtext.toLowerCase();
        return this.levels.filter(
          (o) => o.name.toLowerCase().includes(lower) || lower.includes(o.name.toLowerCase()),
        );
      },
      selectedLevel: {
        get() {
          return this.levels.find((o) => o.isSelected)?.id;
        },
        set(value) {
          this.levels.forEach((o) => {
            o.isSelected = o.id == value;
          });
        },
      },
    },
    components: {
      EditLevel,
    },
  };
</script>

<style scoped>
  .model-3d {
    background-size: cover;
    background-image: url(../../../images/3d-black.png);
    width: 23px;
    height: 23px;
    margin-right: 10px;
  }

  .level-dbids {
    width: auto;
    margin: 5px;
    border-radius: 5px;
    color: white;
    font-size: 13px;
    padding: 1px 5px;
  }
  v-data-table {
    position: relative;
  }
  v-data-table .v-data-footer {
    position: fixed;
    bottom: 0;
    width: -webkit-fill-available;
  }
</style>
